import React, { useEffect, useState } from 'react';
import { Tag, Clock, Calendar, CalendarOff, User } from 'lucide-react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { it } from 'date-fns/locale';
import { SearchableSelect } from '../../ui/searchable-select';
import { CalendarModal } from '../../calendar/CalendarModal';
import type { AttendeeUserOption } from './AttendeesPicker';
import { useActivityTypeOptions } from './useActivityTypeOptions';
import { useActivityTypesStore } from '../../../lib/stores/activityTypesStore';
import { buildActivityTypeSelectGroups } from './activityTypeSelect';
import { PropRow } from './ui/PropRow';
import { resolveAssigneeCalendarHint } from './assigneeCalendarHint';
import { AssigneeAvailabilityHint } from '../../calendar/team/AssigneeAvailabilityHint';
import type { ResolvedSyncSettings } from './types';
import { calendarsButtonLabel, filterBookingOptions, type BookingResolution } from '../../../lib/calendar/booking';
import { useCalendarSharingStore } from '../../../lib/stores/calendarSharingStore';
import { useEnsureSharing } from '../../../lib/calendar/team/useEnsureSharing';
import { usePermissions } from '../../../lib/hooks/usePermissions';

registerLocale('it', it);

/** Campi del form attività gestiti da questo componente (sottoinsieme di `ActivitySaveInput.activityData`, types.ts — esclude `description`, di competenza di NoteTab/shell). */
export interface ActivityTabData {
  title: string;
  type: string;
  status: string;
  startDateTime: Date;
  endDateTime: Date | null;
  assigned_to: string | null;
  /** Esito della task (`sales_activities.outcome`): mostrato e modificabile solo se Completata. */
  outcome?: string;
}

export interface ActivityTabProps<T extends ActivityTabData = ActivityTabData> {
  activityData: T;
  onActivityDataChange: React.Dispatch<React.SetStateAction<T>>;
  titleError?: boolean;
  /** Invocata quando l'utente digita nel titolo, per azzerare l'errore lato shell (fonte: `setTitleError(false)` inline). */
  onTitleErrorClear?(): void;
  activityTitleRequired?: boolean;
  /** Riga attività in editing — SOLO per `CalendarModal` (titolo pannello, highlight/hide evento). `null`/assente = nuova attività. */
  editingActivityId?: string | null;
  editingActivityGoogleEventId?: string | null;
  /**
   * Gruppi di opzioni EXTRA per la select "Tipo", inseriti fra i tipi di sistema e i tipi
   * custom, ognuno reso come `<optgroup>` — stessa posizione della fonte, dove il
   * gruppo AlfaDocs (`alfadocs_*`) sta fra gli `<option>` di sistema e i tipi personalizzati
   * (LeadStatusUpdateDrawer.tsx:1974-1989). Additivo: assente = select invariata.
   */
  extraTypeGroups?: { label: string; options: { value: string; label: string }[] }[];
  /**
   * true quando il form è stato pre-popolato da `initialActivityData` (suggerimento AI) —
   * mostra la chip ghost "Suggerita dall'AI" accanto al titolo (redesign T3, M9).
   */
  aiPrefilled?: boolean;
  /**
   * Utenti CRM per la riga "Assegnata a" (mini-avatar + select). Prima arrivava implicito
   * da `CalendarSectionProps`: la sezione calendario è stata spostata nella shell (redesign
   * T5, step 4) e questo resta l'unico dato di contorno che serve al tab.
   */
  users: AttendeeUserOption[];
  /**
   * Sync calendario della persona scelta in "Assegnata a" (le stesse che la shell passa a
   * `CalendarSection`, ricaricate a ogni cambio assegnatario). Servono alla riga discreta
   * "X non ha un calendario collegato" sotto la select: l'avviso sta dove si sceglie la
   * persona, non in fondo al drawer. `null`/assente = ancora in caricamento, niente riga.
   */
  assigneeSyncSettings?: ResolvedSyncSettings | null;
  /** Id dell'utente corrente: se e' lui l'assegnatario la riga parla in seconda persona. */
  currentUserId?: string | null;
  /** Regola "Fissare gli appuntamenti": con una regola l'elenco propone solo le persone ammesse. */
  booking?: BookingResolution | null;
  /** Persona gia' salvata (in modifica): resta nell'elenco anche se fuori dalla regola. */
  keepAssigneeId?: string | null;
  /** Messaggio accanto a "Nel calendario di" (scelta obbligatoria o persona non ammessa). */
  assigneeError?: string | null;
}

// Fino a 8 ore: richiesta Barf Italia via Unidevs (15/09/2026). Stesso elenco di TaskCreationModal.
const presetDurations = [15, 30, 45, 60, 90, 120, 180, 240, 300, 360, 420, 480];

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} minuti`;
  if (m === 0) return h === 1 ? '1 ora' : `${h} ore`;
  return `${h} or${h > 1 ? 'e' : 'a'} ${m} min`;
}

/** Iniziali per il mini-avatar dell'assegnatario (mockup `.sc-mini-avatar`). */
function getAssigneeInitials(user: { full_name?: string | null; email?: string | null }): string {
  const source = user.full_name?.trim() || user.email || '';
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

/**
 * Tab "Attività" del drawer condiviso Attività/Nota. Componente CONTROLLATO: stato del
 * form via `activityData`/`onActivityDataChange`, nessuna query interna eccetto
 * `useActivityTypesStore` (read-only, come nella fonte — serve per l'edge case del tipo
 * custom disattivato, vedi `activityTypeSelect.ts`).
 *
 * Redesign T3: righe di proprietà stile Notion (`PropRow`) al posto della griglia a
 * label+select; titolo in testa al tab come titolo di pagina. Fonte originaria:
 * LeadStatusUpdateDrawer.tsx :1948-2408, ESCLUSI i blocchi già coperti da
 * `CalendarSection` (T9: sync/calendario/provider meeting/attendees/organizer, :2022-2265)
 * e il blocco "Commerciale assegnato al Lead" (:2358-2376, entity-specific — assegnato a
 * `statusSection`/Task 13 dal piano).
 */
export function ActivityTab<T extends ActivityTabData = ActivityTabData>({
  activityData,
  onActivityDataChange,
  titleError,
  onTitleErrorClear,
  activityTitleRequired = true,
  editingActivityId = null,
  editingActivityGoogleEventId = null,
  extraTypeGroups = [],
  aiPrefilled = false,
  users,
  assigneeSyncSettings = null,
  currentUserId = null,
  booking = null,
  keepAssigneeId = null,
  assigneeError = null,
}: ActivityTabProps<T>) {
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  // true = il calendario si apre direttamente in vista Team (pulsante sotto "Nel calendario di")
  const [calendarTeamStart, setCalendarTeamStart] = useState(false);
  // Calendari dei colleghi: caricati una volta, servono a sapere se c'e' la vista Team
  useEnsureSharing(true);
  const canSeeTeam = useCalendarSharingStore((s) => Object.keys(s.visible).length > 1);
  // Avvisi su Google/Outlook solo a chi puo' collegarli (permesso "Collega Google o Outlook")
  const { hasPermission } = usePermissions();
  const canSyncCalendar = hasPermission('calendar', 'sync');

  // Durata: concetto puramente UI (deriva end-start), non persistito in ActivityTabData.
  // Inizializzata dal diff start/end se presente (edit di un'attività esistente),
  // fallback 15 min — fonte :201/:784-793.
  const [activityDuration, setActivityDuration] = useState<number>(() => {
    if (activityData.endDateTime) {
      const diffMinutes = Math.round(
        (activityData.endDateTime.getTime() - activityData.startDateTime.getTime()) / (60 * 1000)
      );
      if (diffMinutes > 0) return diffMinutes;
    }
    return 15;
  });
  // In modifica i dati veri arrivano dopo il primo render: la durata va riallineata a fine - inizio,
  // altrimenti resta 15 minuti e al salvataggio accorcia l'appuntamento
  const startMs = activityData.startDateTime?.getTime();
  const endMs = activityData.endDateTime?.getTime();
  useEffect(() => {
    if (startMs == null || endMs == null) return;
    const diff = Math.round((endMs - startMs) / (60 * 1000));
    if (diff > 0) setActivityDuration(diff);
  }, [startMs, endMs]);

  const typeOptions = useActivityTypeOptions();
  const { customTypes } = useActivityTypesStore();
  const { standardOptions, customOptions, selectedIsInactiveCustom } = buildActivityTypeSelectGroups(
    typeOptions,
    customTypes,
    activityData.type
  );

  // Fonte :272-283: filtra solo le ore passate del giorno corrente, giorni futuri liberi.
  const filterPassedTime = (time: Date) => {
    const now = new Date();
    if (activityData.startDateTime.toDateString() !== now.toDateString()) return true;
    return now.getTime() < time.getTime();
  };

  // Fonte :545-565, adattata al contratto Date già combinate (niente più due_date/due_time stringa).
  const handleDateTimeChange = (date: Date | null) => {
    if (!date) return;
    const endDate = new Date(date.getTime() + activityDuration * 60 * 1000);
    onActivityDataChange((prev) => ({ ...prev, startDateTime: date, endDateTime: endDate }));
  };

  // Fonte :577-588.
  const handleDurationChange = (minutes: number) => {
    setActivityDuration(minutes);
    const endDate = new Date(activityData.startDateTime.getTime() + minutes * 60 * 1000);
    onActivityDataChange((prev) => ({ ...prev, endDateTime: endDate }));
  };

  // Fonte :591-611 — durata reale dallo slot, nessun arrotondamento; il calendario NON si chiude qui.
  const handleCalendarConfirm = (start: Date, end: Date) => {
    const durationMinutes = Math.round((end.getTime() - start.getTime()) / (60 * 1000));
    setActivityDuration(durationMinutes);
    onActivityDataChange((prev) => ({ ...prev, startDateTime: start, endDateTime: end }));
  };

  const selectedAssignee = users.find((u) => u.id === activityData.assigned_to);
  // Senza scelta esplicita l'attività va a chi la crea: la riga parla di lui.
  // Con la regola "Fissare gli appuntamenti" il campo vuoto resta vuoto (non e' "a chi crea").
  const effectiveAssigneeId = activityData.assigned_to || (booking ? null : currentUserId);
  // Campo vuoto (regola "Fissare gli appuntamenti"): nessun avviso sul calendario di una persona non scelta
  const assigneeCalendarHint = !effectiveAssigneeId || !canSyncCalendar ? null : resolveAssigneeCalendarHint({
    settings: assigneeSyncSettings,
    assigneeName: selectedAssignee?.full_name || selectedAssignee?.email || null,
    isSelf: !!effectiveAssigneeId && effectiveAssigneeId === currentUserId,
  });

  return (
    <>
      <div className="flex items-center gap-2">
        <input
          type="text"
          id="activity-title"
          aria-label="Titolo attività"
          autoComplete="off"
          value={activityData.title}
          onChange={(e) => {
            const newTitle = e.target.value;
            onActivityDataChange((prev) => ({ ...prev, title: newTitle }));
            if (titleError) onTitleErrorClear?.();
          }}
          className="w-full border-none outline-none bg-transparent text-[17px] font-semibold text-[#37352F] dark:text-white px-1.5 placeholder:text-[rgba(55,53,47,0.45)] dark:placeholder:text-white/40"
          placeholder={activityTitleRequired ? 'Titolo attività' : 'Titolo attività (facoltativo)'}
        />
        {aiPrefilled && (
          <span className="shrink-0 mr-1.5 text-[10px] px-1.5 rounded-full bg-[rgba(55,53,47,0.06)] text-[#787774] dark:bg-white/[0.06] dark:text-gray-400">
            Suggerita dall'AI
          </span>
        )}
      </div>
      {titleError && <p className="px-1.5 text-[11px] text-red-500">Il titolo è obbligatorio</p>}

      <div className="flex flex-col" data-tour="drawer-props">
        <PropRow icon={<Tag className="h-3 w-3" strokeWidth={1.5} />} label="Tipo" wrap>
          <SearchableSelect
            value={activityData.type}
            onValueChange={(value) => {
              onActivityDataChange((prev) => ({ ...prev, type: value }));
            }}
            options={[
              ...standardOptions,
              ...extraTypeGroups.flatMap((group) => group.options),
              ...customOptions,
            ].map((opt) => ({ value: opt.value, label: opt.label }))}
            placeholder="Seleziona tipo..."
            ariaLabel="Tipo"
            className="flex-1 min-w-0"
            triggerClassName={`h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px] ${
              selectedIsInactiveCustom ? 'text-[#787774] dark:text-gray-400' : ''
            }`}
          />
        </PropRow>

        <PropRow icon={<Clock className="h-3 w-3" strokeWidth={1.5} />} label="Avanzamento" wrap>
          <SearchableSelect
            value={activityData.status}
            onValueChange={(value) => {
              onActivityDataChange((prev) => ({ ...prev, status: value }));
            }}
            options={[
              { value: 'pending', label: 'In attesa' },
              { value: 'completed', label: 'Completata' },
              { value: 'overdue', label: 'In ritardo' },
            ]}
            placeholder="Seleziona avanzamento..."
            ariaLabel="Avanzamento"
            className="flex-1 min-w-0"
            triggerClassName="h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px]"
          />
        </PropRow>

        {/* Esito della task: e' quello di QUESTA riga (reinizializzato a ogni apertura in
            useDrawerForm), visibile solo quando la task e' completata. */}
        {activityData.status === 'completed' && activityData.outcome !== undefined && (
          <div className="px-1.5 py-1">
            <label htmlFor="activity-outcome" className="mb-1 block text-[12px] text-[#787774] dark:text-gray-400">
              Esito
            </label>
            <textarea
              id="activity-outcome"
              rows={3}
              value={activityData.outcome}
              onChange={(e) => {
                const value = e.target.value;
                onActivityDataChange((prev) => ({ ...prev, outcome: value }));
              }}
              placeholder="Esito della task..."
              className="block w-full resize-y rounded-md border border-[rgba(55,53,47,0.09)] bg-[#F7F7F5] px-2.5 py-1.5 text-[13px] text-[#37352F] placeholder:text-[#9B9A97] focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2] dark:border-[#3F4447] dark:bg-[#373C3F] dark:text-white"
            />
          </div>
        )}

        <PropRow
          icon={<Calendar className="h-3 w-3" strokeWidth={1.5} />}
          label="Data e ora"
          required
          trailingAlwaysVisible
          trailing={
            <button
              type="button"
              onClick={() => { setCalendarTeamStart(false); setShowCalendarModal(true); }}
              className="inline-flex items-center gap-1 rounded px-1 hover:bg-[rgba(55,53,47,0.08)] hover:text-[#37352F] dark:hover:bg-white/10 dark:hover:text-white"
            >
              <Calendar className="h-3 w-3" strokeWidth={1.5} />
              Vedi calendario
            </button>
          }
        >
          {/* `customInput` e' l'unico modo per dare un `aria-label` all'input di
              react-datepicker 6: `renderDateInput` clona l'input e inoltra solo
              aria-describedby/invalid/labelledby/required, quindi un `aria-label`
              passato al DatePicker verrebbe scartato. Gli attributi gia' presenti
              sull'elemento custom sopravvivono al clone. */}
          <DatePicker
            customInput={<input type="text" aria-label="Data e ora" />}
            selected={activityData.startDateTime}
            onChange={handleDateTimeChange}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            timeCaption="Ora"
            dateFormat="dd/MM/yyyy HH:mm"
            locale="it"
            placeholderText="Seleziona data e ora"
            className="w-full bg-transparent border-none outline-none text-[13px] text-[#37352F] dark:text-white cursor-pointer p-0 appearance-none"
            wrapperClassName="w-full"
            popperClassName="z-50"
            portalId="root-portal"
            minDate={new Date()}
            filterTime={filterPassedTime}
          />
        </PropRow>

        <PropRow icon={<Clock className="h-3 w-3" strokeWidth={1.5} />} label="Durata" wrap>
          <SearchableSelect
            value={String(activityDuration)}
            onValueChange={(value) => handleDurationChange(Number(value))}
            options={[
              ...(!presetDurations.includes(activityDuration)
                ? [{ value: String(activityDuration), label: formatDuration(activityDuration) }]
                : []),
              { value: '15', label: '15 minuti' },
              { value: '30', label: '30 minuti' },
              { value: '45', label: '45 minuti' },
              { value: '60', label: '1 ora' },
              { value: '90', label: '1 ora 30 min' },
              { value: '120', label: '2 ore' },
              { value: '180', label: '3 ore' },
              { value: '240', label: '4 ore' },
              { value: '300', label: '5 ore' },
              { value: '360', label: '6 ore' },
              { value: '420', label: '7 ore' },
              { value: '480', label: '8 ore' },
            ]}
            placeholder="Seleziona durata..."
            ariaLabel="Durata"
            className="flex-1 min-w-0"
            triggerClassName="h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px]"
          />
        </PropRow>

        {/* `wrap`: rimuove l'overflow-hidden del value column, altrimenti il dropdown di
            SearchableSelect (assoluto, non-portal) verrebbe tagliato dalla riga 30px. */}
        <PropRow icon={<User className="h-3 w-3" strokeWidth={1.5} />} label="Nel calendario di" wrap>
          {selectedAssignee && (
            <span className="w-4 h-4 rounded-full bg-[#DDEDFB] dark:bg-[rgba(35,131,226,0.25)] text-[#1D6FBF] dark:text-[#7CB8EE] text-[8px] font-semibold inline-flex items-center justify-center shrink-0">
              {getAssigneeInitials(selectedAssignee)}
            </span>
          )}
          <SearchableSelect
            value={activityData.assigned_to || ''}
            onValueChange={(value) => {
              onActivityDataChange((prev) => ({ ...prev, assigned_to: value || null }));
            }}
            options={filterBookingOptions(users, booking, keepAssigneeId).map((user) => ({
              value: user.id,
              label: user.full_name || user.email || user.id,
            }))}
            placeholder={booking ? 'Scegli la persona...' : 'Seleziona utente...'}
            ariaLabel="Nel calendario di"
            personNames
            className="flex-1 min-w-0"
            triggerClassName="h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px]"
          />
        </PropRow>
        {/* Sempre visibile, subito sotto la scelta: apre i calendari (in vista Team se ne vedo piu' di uno);
            il clic su uno spazio libero riempie giorno, ora e persona. */}
        <div className="pl-[8.75rem] pr-1.5 pt-0.5 pb-1">
          <button
            type="button"
            onClick={() => { setCalendarTeamStart(canSeeTeam); setShowCalendarModal(true); }}
            data-testid="open-team-calendars"
            className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-[rgba(55,53,47,0.16)] bg-white px-2.5 py-1 text-left text-[12.5px] font-medium text-[#37352F] shadow-[0_1px_2px_rgba(15,15,15,0.04)] hover:bg-[rgba(55,53,47,0.06)] dark:border-white/15 dark:bg-transparent dark:text-white dark:hover:bg-white/10"
          >
            <Calendar className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
            <span>{calendarsButtonLabel({ hasBookingRule: !!booking, canSeeTeam })}</span>
          </button>
        </div>
        {assigneeError && (
          <p className="pl-[8.75rem] pr-1.5 -mt-0.5 pb-1 text-[11.5px] leading-snug text-red-500" data-testid="assignee-booking-error" role="alert">
            {assigneeError}
          </p>
        )}
        {/* Riga discreta, allineata alla colonna del valore (label w-32 + padding della riga):
            compare solo quando la persona scelta non ha un calendario pronto. Lo stesso
            avviso, per esteso, sta nella sezione Calendario in fondo; qui e' dove serve. */}
        {assigneeCalendarHint && (
          <div
            className="flex items-start gap-1 pl-[8.75rem] pr-1.5 -mt-0.5 pb-1 text-[11.5px] leading-snug text-[#B45309] dark:text-[#E8B04B]"
            data-testid="assignee-calendar-hint"
          >
            <CalendarOff className="h-3 w-3 mt-[2px] shrink-0" strokeWidth={1.5} />
            <span>{assigneeCalendarHint}</span>
          </div>
        )}
        {/* Giornata della persona scelta: impegni, orario scelto e avviso se si sovrappone (non blocca) */}
        <div className="pl-[8.75rem] pr-1.5 pb-1">
          <AssigneeAvailabilityHint
            assigneeId={effectiveAssigneeId || null}
            assigneeName={selectedAssignee?.full_name || selectedAssignee?.email || null}
            date={`${activityData.startDateTime.getFullYear()}-${String(activityData.startDateTime.getMonth() + 1).padStart(2, '0')}-${String(activityData.startDateTime.getDate()).padStart(2, '0')}`}
            time={`${String(activityData.startDateTime.getHours()).padStart(2, '0')}:${String(activityData.startDateTime.getMinutes()).padStart(2, '0')}`}
            durationMin={activityDuration}
            excludeActivityId={editingActivityId || null}
            onUseTime={(hhmm) => {
              const [h, m] = hhmm.split(':').map(Number);
              const start = new Date(activityData.startDateTime);
              start.setHours(h, m, 0, 0);
              handleCalendarConfirm(start, new Date(start.getTime() + activityDuration * 60000));
            }}
          />
        </div>
      </div>

      {/* La sezione Calendario NON è più resa qui: la monta la shell DOPO l'editor
          (ordine del redesign: titolo → proprietà → Descrizione → Calendario). */}
      <CalendarModal
        isOpen={showCalendarModal}
        onClose={() => setShowCalendarModal(false)}
        initialTeam={calendarTeamStart}
        title={editingActivityId ? 'Modifica data/ora attività' : 'Seleziona uno slot dal calendario'}
        size="full"
        mode="sidepanel"
        defaultView="timeGridThreeDay"
        highlightEventId={editingActivityId ? `sales_activity_${editingActivityId}` : undefined}
        hideEventId={editingActivityGoogleEventId || undefined}
        onConfirmSelection={handleCalendarConfirm}
        onTeamPick={(slot) => {
          handleCalendarConfirm(slot.start, slot.end);
          onActivityDataChange((prev) => ({ ...prev, assigned_to: slot.userId }));
        }}
        slotTitle={activityData.title}
        slotDateTime={activityData.startDateTime}
        slotDuration={activityDuration}
      />
    </>
  );
}
